/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
import axios from 'axios';
import { ProjectCertificationStorePayload } from '@resources/scripts/models/projectCertificationStorePayload.model';
import transactionApi from '@resources/scripts/services/api/transaction.api';
import { PROJECT_CERTIFICATION_FORM } from '../mutation-types';
import projectCertificationApi from '../../services/api/projectCertification.api';
import projectCertificationService from '../../services/projectCertification.service';
import { setState } from '../mutationHelpers';

const namespaced = true;

const state = {
  formFields: {
    id: null,
    submitterEmail: '',
    submitterName: '',
    qcpProjectNumber: '',
    projectName: '',
    qcpContractAmount: '',
    projectCity: '',
    projectStateProvince: '',
    estimatedCompletionDate: null,
    qcpLicenseeOrApplicantCompany: '',
    woodworkerContactFirstName: '',
    woodworkerContactLastName: '',
    projectManagerEmail: '',
    woodworkerAddressLineOne: '',
    woodworkerAddressLineTwo: '',
    woodworkerCity: '',
    woodworkerStateProvince: '',
    woodworkerZipPostal: '',
    woodworkerPhone: '',
    woodworkerFax: '',

    firmName: '',
    firmContactFirstName: '',
    firmContactLastName: '',
    firmContactEmail: '',
    firmCity: '',
    firmStateProvince: '',

    generalContractorName: '',
    generalContractorCity: '',
    generalContractorStateProvince: '',

    projectStandards: 'Architectural Design',
    cabinetMfg: '',
    woodworkMfg: '',
    doorSupplier: '',
    finisher: '',
    installedBy: 'Woodworker',

    certificationInfo: [
      {
        showHeading: true,
        name: 'Architectural Woodwork Standards (AWS) Section [2019]',
        items: [
          {
            name: 'Factory Finishing',
            key: '5',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Interior and Exterior Millwork',
            key: '6',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Stairwork and Rails',
            key: '7',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Wall Surfacing',
            key: '8',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Doors (enter quantity of doors)',
            key: '9',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Casework',
            key: '10',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Countertops',
            key: '11',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Historic Restoration Work',
            key: '12',
            grade: 'Custom',
            quantity: 0,
          },
        ],
      },
      {
        showHeading: true,
        name: 'AWI Standards: Current Edition',
        items: [
          {
            name: 'Finish Carpentry/Installation',
            key: '0620',
            grade: 'Custom',
            quantity: 0,
          },
          {
            name: 'Architectural Wood Casework',
            key: '0641',
            grade: 'Custom',
            quantity: 0,
          },
        ],
      },
      {
        showHeading: false,
        items: [
          {
            name: 'Certificate of Compliance (Quantity)',
            quantity: 0,
          },
        ],
      },
    ],

    firmAttests: false,
    signature: '',
    title: '',
    email: '',

    transactionMinPrice: 0,
    transactionMaxPrice: 0,

    billingFirstName: '',
    billingLastName: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingPhone: '',
    billingEmail: '',
    nameOnCard: '',
    cardNumber: '',
    cardCVV: '',
    cardExpMonth: '',
    cardExpYear: '',
    billingComments: '',

    projectId: 0,
    woodworkerCompanyId: 0,
    woodworkerContactId: 0,
    generalContractorCompanyId: 0,
    designCompanyId: 0,
    designContactId: 0,
  } as ProjectCertificationStorePayload,
  currentPage: 1,
  countryOptions: [],
  submitting: false,
  submitted: false,
};

const getters = {
  transactionAmount: (storeState) => {
    let contractAmountInt = 0;
    if (storeState.formFields.qcpContractAmount) {
      contractAmountInt = Number.parseFloat(storeState.formFields.qcpContractAmount);
    }
    const percentageOfContractAmount = (contractAmountInt / 100) * 0.5;
    const contractAmount = Number.parseInt(storeState.formFields.qcpContractAmount, 10) || 0;
    if (contractAmount < 100000) return storeState.formFields.transactionMinPrice.toFixed(2);
    if (percentageOfContractAmount > storeState.formFields.transactionMaxPrice) return storeState.formFields.transactionMaxPrice.toFixed(2);

    return percentageOfContractAmount.toFixed(2);
  },
  countryOptions: storeState => storeState.countryOptions,
  stateProvinceOptions: storeState => countryName => (storeState.countryOptions.find(x => x.name === countryName) ? storeState.countryOptions.find(x => x.name === countryName).stateProvinces : []),
  formFields: storeState => storeState.formFields,
  currentPage: storeState => storeState.currentPage,
  submitting: storeState => storeState.submitting,
  submitted: storeState => storeState.submitted,
};

const mutations = {
  [PROJECT_CERTIFICATION_FORM.UPDATE_COUNTRY_OPTIONS]: setState('countryOptions'),
  [PROJECT_CERTIFICATION_FORM.CHANGE_CURRENT_PAGE]: setState('currentPage'),
  [PROJECT_CERTIFICATION_FORM.TOGGLE_SUBMITTED_STATE]: setState('submitted'),
  [PROJECT_CERTIFICATION_FORM.TOGGLE_SUBMITTING_STATE]: setState('submitting'),

  [PROJECT_CERTIFICATION_FORM.FORM_FIELDS](formState, fields) {
    Object.assign(formState.formFields, fields);
  },
};

const actions = {
  existingInfoFieldUpdate({ commit }, payload) {
    commit(PROJECT_CERTIFICATION_FORM.FORM_FIELDS, payload);
  },

  async goToNextPage({ dispatch, commit, state: actionState }) {
    commit(PROJECT_CERTIFICATION_FORM.CHANGE_CURRENT_PAGE, actionState.currentPage + 1);
    await dispatch('saveStep');
    window.scrollTo(0, 0);
  },

  goToPrevPage({ commit, state: actionState }) {
    commit(PROJECT_CERTIFICATION_FORM.CHANGE_CURRENT_PAGE, actionState.currentPage - 1);
    window.scrollTo(0, 0);
  },

  async getCountries({ commit }) {
    const { data } = await axios.get('/api/countries');
    commit(PROJECT_CERTIFICATION_FORM.UPDATE_COUNTRY_OPTIONS, data);
  },

  saveStep({ commit, state: actionState }) {
    return new Promise(async (resolve, reject) => {
      const formFields = actionState.formFields as ProjectCertificationStorePayload;
      try {
      if (formFields.id) {
        await projectCertificationApi.updateSubmission(formFields.id, formFields);
      } else {
        const { data } = await projectCertificationApi.create(formFields);
        commit(PROJECT_CERTIFICATION_FORM.FORM_FIELDS, data);
      }
      resolve();
    } catch (error) {
      reject(error);
    }
    });
  },

  submitForm({ commit, state: actionState, getters: actionGetters }) {
    return new Promise(async (resolve, reject) => {
      commit(PROJECT_CERTIFICATION_FORM.TOGGLE_SUBMITTING_STATE, true);
      const formFields = actionState.formFields as ProjectCertificationStorePayload;
      try {
        const { data: transactionData } = await transactionApi.addTransaction({
          type: 'PROJECT_CERTIFICATION',
          nameOnCard: actionState.formFields.nameOnCard,
          companyName: formFields.qcpLicenseeOrApplicantCompany,
          billingAddress1: actionState.formFields.billingAddressLine1,
          billingAddress2: actionState.formFields.billingAddressLine2,
          billingCity: actionState.formFields.billingCity,
          billingStateProvince: actionState.formFields.billingState,
          billingZip: actionState.formFields.billingZip,
          billingPhone: actionState.formFields.billingPhone,
          billingEmail: actionState.formFields.billingEmail,
          cardNumber: actionState.formFields.cardNumber,
          CardExpireMonth: actionState.formFields.cardExpMonth,
          cardExpireYear: actionState.formFields.cardExpYear,
          cVV2: actionState.formFields.cardCVV,
          comments: actionState.formFields.billingComments,
          description: 'Project Certification Labels',
          amount: actionGetters.transactionAmount,
        });
        const { data: formResult } = await projectCertificationApi.updateSubmission(formFields.id, formFields);
        await projectCertificationApi.associateTransaction(formResult, transactionData.id);
        await projectCertificationApi.submitFinal(formFields.id, formFields);
        commit(PROJECT_CERTIFICATION_FORM.TOGGLE_SUBMITTED_STATE, true);
        try {
          // eslint-disable-next-line @typescript-eslint/no-angle-bracket-type-assertion
          (<any>window).gtag('config', 'UA-69171335-1', {
            page_title: 'Project Certification - Submitted',
            page_path: '/forms/project-certification/submitted',
          });
        } catch (error) {}
        resolve();
      } catch (error) {
        commit(PROJECT_CERTIFICATION_FORM.TOGGLE_SUBMITTING_STATE, false);
        reject(error);
      }
    });
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};